<template>
    <div v-if="currentReport">
        <v-row>
            <v-col cols="12">
                <h1>{{ t('reports::trackers.grid-title.mhbu-results') }}</h1>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="12" xl="9" md="12">
                <v-row class="table-head">
                    <v-col cols="12" lg="6" class="table-head-filters">
                        <v-row>
                            <v-col cols="12" lg="9">
                                <b-filter-date
                                    ref="dateFilter"
                                    v-model="dateFilter"
                                    type="month"
                                    :label="t('reports::filters.label.date')"
                                    @change="setReportFilter"
                                    @clear="filterDateClear"
                                ></b-filter-date>
                            </v-col>
                        </v-row>
                    </v-col>
                    <v-col cols="12" lg="6" class="table-head-actions">
                        <v-btn
                            v-if="can('api.v1.trackers.reports.mhbu.store')"
                            color="primary"
                            small
                            rounded
                            text
                            :loading="generateReportLoading"
                            @click="generateReport"
                            >{{ t('reports::button.generate_report') }}</v-btn
                        >
                        <v-btn
                            v-if="can('api.v1.trackers.reports.mhbu.index')"
                            color="primary"
                            small
                            rounded
                            class="ml-2"
                            @click="fetchReportsList"
                            >{{ t('reports::button.view_reports') }}</v-btn
                        >
                    </v-col>
                </v-row>
                <v-data-table
                    v-if="items"
                    fixed-header
                    :headers="currentReportHeaders"
                    :items="items"
                    :loading="loading"
                    :items-per-page="10"
                    :hide-default-footer="false"
                    :footer-props="{
                        'items-per-page-options': [15, 30, 50, -1],
                        'items-per-page-text': t('datatable::pagination.items.per.page.text'),
                    }"
                >
                    <template #[`item.completed_by.name`]="{ item }">
                        <div v-if="item.completed_by && item.completed_by.name">
                            <div
                                v-if="!_.isEmpty(item.completed_by.id) && can('users.get')"
                                class="link"
                                @click="$root.goTo(dmsUserItemUrl(item.completed_by))"
                            >
                                {{ item.completed_by.name }}
                            </div>
                            <div v-else>
                                {{ item.completed_by.name }}
                            </div>
                        </div>
                        <div v-else>
                            {{ t('default::label.na') }}
                        </div>
                    </template>
                    <template #[`item.patient.dms_id`]="{ item }">
                        <div v-if="can('patients.get')" class="link" @click="$root.goTo(patientsItemUrl(item.patient))">
                            {{ item.patient.dms_id }}
                        </div>
                        <div v-else>
                            {{ item.patient.dms_id }}
                        </div>
                    </template>
                    <template #[`item.registration_date`]="{ item }">
                        {{
                            item.registration_date
                                ? moment(item.registration_date).format($root.dateFormat)
                                : t('default::label.na')
                        }}
                    </template>
                    <template #[`item.registration_type`]="{ item }">
                        {{
                            item.registration_type
                                ? t(`patients::status.${item.registration_type}`)
                                : t('default::label.na')
                        }}
                    </template>
                    <template #[`item.pharmacy_groups[0].group_name`]="{ item }">
                        <div v-if="item.pharmacy_groups && !!item.pharmacy_groups.length">
                            <div
                                v-if="can('pharmacy-groups.get')"
                                class="link"
                                @click="$root.goTo(pharmacyGroupsItemUrl(item.pharmacy_groups[0]))"
                            >
                                {{ item.pharmacy_groups[0].group_name }}
                            </div>
                            <div v-else>
                                {{ item.pharmacy_groups[0].group_name }}
                            </div>
                        </div>
                        <div v-else>
                            {{ t('default::label.na') }}
                        </div>
                    </template>
                    <template #[`item.pharmacy_site.site_name`]="{ item }">
                        <div v-if="item.pharmacy_site && item.pharmacy_site.site_name">
                            <div
                                v-if="can('pharmacy-sites.get')"
                                class="link"
                                @click="$root.goTo(pharmacySitesItemUrl(item.pharmacy_site))"
                            >
                                {{ item.pharmacy_site.site_name }}
                            </div>
                            <div v-else>
                                {{ item.pharmacy_site.site_name }}
                            </div>
                        </div>
                        <div v-else>
                            {{ t('default::label.na') }}
                        </div>
                    </template>
                    <template #[`item.minutes_to_complete_action`]="{ item }">
                        <v-edit-dialog
                            :ref="`editDialog${item.id}`"
                            :return-value.sync="item.minutes_to_complete_action"
                            @save="saveItemRow(item)"
                        >
                            <v-icon left>mdi-pencil</v-icon>
                            <span class="grid-min">
                                {{ item.minutes_to_complete_action || 0 }} {{ t('reports::trackers.grid.label.min') }}
                            </span>
                            <template #input>
                                <div class="form-minutes">
                                    <div class="input-min">
                                        <v-text-field
                                            :ref="`editDialogInput${item.id}`"
                                            v-model="item.minutes_to_complete_action"
                                            dense
                                            outlined
                                            :rules="[max4len]"
                                            rounded
                                            type="number"
                                        ></v-text-field>
                                        <label for="minutes">{{ t('reports::trackers.grid.label.min') }}</label>
                                    </div>
                                    <div class="input-actions">
                                        <v-tooltip top transition="v-fab-transition">
                                            <template #activator="{ on, attrs }">
                                                <v-btn
                                                    color="primary"
                                                    icon
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    @click="fakeSaveItemRow(item)"
                                                >
                                                    <v-icon>mdi-check</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>{{ t('default::button.save') }}</span>
                                        </v-tooltip>
                                        <v-tooltip top transition="v-fab-transition">
                                            <template #activator="{ on, attrs }">
                                                <v-btn
                                                    color="disabled-color"
                                                    icon
                                                    v-bind="attrs"
                                                    v-on="on"
                                                    @click="closeDialog(item)"
                                                >
                                                    <v-icon>mdi-close</v-icon>
                                                </v-btn>
                                            </template>
                                            <span>{{ t('default::button.close') }}</span>
                                        </v-tooltip>
                                    </div>
                                </div>
                            </template>
                        </v-edit-dialog>
                    </template>
                </v-data-table>
            </v-col>
            <v-col v-if="meta" cols="12" xl="3" md="12">
                <div class="mhbu-meta">
                    <h3>{{ t('reports::trackers.grid.label.count') }}</h3>
                    <div class="spacer"></div>
                    <div class="mhbu-meta-list">
                        <div class="mhbu-meta-item">
                            <div class="item header">
                                <div class="item-label">{{ t('reports::trackers.grid.label.registration_type') }}</div>
                                <div class="item-count">{{ t('reports::trackers.grid.label.total') }}</div>
                            </div>
                            <div class="item">
                                <div class="item-label">{{ t('reports::trackers.status.registered') }}</div>
                                <div class="item-count">{{ meta.registered }}</div>
                            </div>
                            <div class="item">
                                <div class="item-label">{{ t('patients::status.transferred_in') }}</div>
                                <div class="item-count">{{ meta.transferred_in }}</div>
                            </div>
                            <div class="item">
                                <div class="item-label">{{ t('patients::status.transferred_out') }}</div>
                                <div class="item-count">{{ meta.transferred_out }}</div>
                            </div>
                            <div class="item">
                                <div class="item-label">{{ t('patients::status.deregistered') }}</div>
                                <div class="item-count">{{ meta.deregistered }}</div>
                            </div>
                            <div class="item colored">
                                <div class="item-label">{{ t('patients::status.total_in') }}</div>
                                <div class="item-count">{{ meta.total_in }}</div>
                            </div>
                            <div class="item colored">
                                <div class="item-label">{{ t('patients::status.total_out') }}</div>
                                <div class="item-count">{{ meta.total_out }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </v-col>
        </v-row>
        <mhbu-reports-list-modal ref="previewModal"></mhbu-reports-list-modal>
    </div>
</template>

<script>
import { isEmpty } from 'lodash/lang';

export default {
    name: 'MhbuReportsComponent',
    data() {
        return {
            dateFilter: null,
            hiddenColumns: ['id'],
            max4len: (v) => `${v}`.length <= 4 || t('default::validation.max4len'),
            generateReportLoading: false,
        };
    },
    computed: {
        loading: {
            get() {
                return this.$store.state.trackers.loading;
            },
            set(loading) {
                return this.$store.dispatch('trackers/setLoading', loading);
            },
        },
        currentReportHeaders() {
            const columns = [
                {
                    text: t('reports::trackers.grid.label.id'),
                    value: 'id',
                    sortable: false,
                    width: 170,
                },
                {
                    text: t('reports::trackers.grid.label.completed_by'),
                    value: 'completed_by.name',
                    fields: 'completed_by',
                    sortable: true,
                    width: 200,
                },
                {
                    text: t('reports::trackers.grid.label.patient_id'),
                    value: 'patient.dms_id',
                    fields: 'patient',
                    sortable: true,
                    width: 120,
                },
                {
                    text: t('reports::trackers.grid.label.registration_date'),
                    value: 'registration_date',
                    sortable: true,
                    width: 150,
                },
                {
                    text: t('reports::trackers.grid.label.registration_type'),
                    value: 'registration_type',
                    sortable: true,
                    width: 150,
                },
                {
                    text: t('reports::trackers.grid.label.pharmacy_groups'),
                    value: 'pharmacy_groups[0].group_name',
                    fields: 'pharmacy_groups',
                    sortable: true,
                    width: 200,
                },
                {
                    text: t('reports::trackers.grid.label.pharmacy_site'),
                    value: 'pharmacy_site.site_name',
                    fields: 'pharmacy_site',
                    sortable: true,
                    width: 200,
                },
                {
                    text: t('reports::trackers.grid.label.minutes_to_complete_action'),
                    value: 'minutes_to_complete_action',
                    sortable: true,
                    align: 'center',
                    width: 140,
                },
            ];
            return columns.filter((x) => !this.hiddenColumns.includes(x.value));
        },
        currentReport() {
            return this.$store.state.trackers.currentReport;
        },
        currentReportType() {
            return this.$store.state.trackers.type;
        },
        currentReportFitler: {
            get() {
                return this.$store.state.trackers.currentReportFilter;
            },
            set(filter) {
                return this.$store.dispatch('trackers/setCurrentReportFilter', filter);
            },
        },
        items() {
            return this.$store.state.trackers.currentReportItems;
        },
        itemsMinu() {
            return this.$store.state.trackers.currentReportItems;
        },
        meta() {
            return this.$store.state.trackers.currentReportMeta;
        },
    },
    watch: {},
    mounted() {
        if (!this.currentReportFitler) {
            this.setReportFilter();
        }
    },
    methods: {
        isEmpty,
        fetchReportsList() {
            this.$store.dispatch('trackers/fetchMhbuReportsList', new Object());
            this.$refs.previewModal.viewReportsModal = true;
        },
        generateReport() {
            this.generateReportLoading = true;
            this.$store
                .dispatch('trackers/generateMhbuReport', this.currentReportFitler)
                .then(() => {
                    this.$root.notify(
                        'info',
                        t('default::notification.success'),
                        t('reports::trackers.mhbu.report.generate')
                    );
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => _.delay(() => (this.generateReportLoading = false), 400));
        },
        filterDateClear() {
            this.dateFilter = null;
            this.loading = true;
            _.delay(() => {
                this.dateFilter = `${moment(new Date()).format('Y-MM')}`;
                this.loading = false;
                this.setReportFilter();
            }, 1000);
        },
        fakeSaveItemRow(item) {
            const input = this.$refs[`editDialogInput${item.id}`];
            if (!input.valid) {
                return;
            }
            this.$refs[`editDialog${item.id}`].save(item.minutes_to_complete_action);
            this.$refs[`editDialog${item.id}`].$emit('save');
        },
        saveItemRow(item) {
            const input = this.$refs[`editDialogInput${item.id}`];
            const dialog = this.$refs[`editDialog${item.id}`];
            if (!input.valid) {
                _.delay(() => {
                    dialog.isActive = true;
                }, 300);
                return;
            }
            const data = {
                data: {
                    id: item.id,
                    minutes_to_complete_action: parseInt(item.minutes_to_complete_action),
                },
            };
            axios
                .put(route('api.v1.trackers.mhbu-results.update'), { data })
                .then(() => {
                    this.$root.notify(
                        'info',
                        t('default::notification.success'),
                        t('reports::trackers.mhbu.item.updated')
                    );
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                });
        },
        closeDialog(item) {
            const dialog = this.$refs[`editDialog${item.id}`];
            if (!dialog) {
                return;
            }
            dialog.$emit('close');
            dialog.isActive = false;
        },
        setReportFilter(filter = null) {
            if (!filter) {
                this.dateFilter = `${moment(new Date()).format('Y-MM')}`;
            }
            const year = parseInt(moment(filter).format('Y')) || parseInt(moment(new Date()).format('Y'));
            const month = parseInt(moment(filter).format('M')) || parseInt(moment(new Date()).format('M'));
            this.currentReportFitler = { year, month };
            this.$refs.dateFilter.onClose();
            return this.loadReport();
        },
        loadReport() {
            if (!this.currentReport && !this.currentReportFitler) {
                return;
            }
            this.loading = true;
            axios
                .get(route(this.currentReport.route, this.currentReportFitler))
                .then((response) => {
                    const data = _.clone(response.data.data);
                    const meta = _.clone(response.data.meta);
                    if (data) {
                        this.$store.dispatch('trackers/setCurrentReportItems', data);
                    }
                    if (meta) {
                        this.$store.dispatch('trackers/setCurrentReportMeta', meta);
                    }
                })
                .catch((error) => {
                    this.$root.notify('error', error.response.data.error);
                })
                .finally(() => {
                    _.delay(
                        () => {
                            this.loading = false;
                        },
                        300,
                        'latter'
                    );
                });
        },
        dmsUserItemUrl(item) {
            return route('dms-users.get', { id: item.id });
        },
        userItemUrl(item) {
            return route('users.get', { id: item.id });
        },
        patientsItemUrl(item) {
            return route('patients.get', { id: item.id });
        },
        pharmacyGroupsItemUrl(item) {
            return route('pharmacy-groups.get', { id: item.id });
        },
        pharmacySitesItemUrl(item) {
            return route('pharmacy-sites.get', { id: item.id });
        },
    },
};
</script>
